<template>
  <div class="product-usages">
    <div class="product-usages__selects">
      <h2 class="product-usages__header">{{ i18n._('Culture et usage') }}</h2>

      <select
        v-model="selected_crop_id"
        class="product-usages__select"
        required
        data-test-select-crop
        @change="fetchUsages"
      >
        <option value="" disabled>{{ i18n._('Choisir une culture') }}</option>
        <option v-for="crop in crops" :key="'crop-' + crop.crop_id" :value="crop.crop_id">{{ crop.crop_name }}</option>
      </select>

      <select
        v-model="selected_target_id"
        class="product-usages__select"
        required
        data-test-select-target
        @change="fetchMatchingUsages"
      >
        <option value="" disabled>{{ i18n._('Choisir une cible') }}</option>
        <option v-for="target in targets" :key="`target-${target.target_id}`" :value="target.target_id">
          {{ target.target_name }}
        </option>
      </select>
    </div>

    <div class="product-usages__results">
      <div v-if="areCropAndTargetSelected" class="product-usages__results-list">
        <p v-if="!matchingUsages && isProductKnown" class="product-usages__warning">
          {{ i18n._("Aucune recommandation d'utilisation n'est disponible pour ce choix de culture et de cible") }}
        </p>
        <div v-else>
          <div v-for="usage in matchingUsages" :key="`usage-${usage.target_id}`" class="product-usages__results-single">
            <p-product-usage-details :usage="usage" />
          </div>
        </div>
      </div>

      <div v-else class="product-usages__no-select">
        <p class="product-usages__no-select-text">
          <span class="strong">{{ i18n._('Choisissez une culture et une cible') }}</span>
          <br />
          {{ i18n._('pour découvrir les usages homologués') }}
        </p>
      </div>

      <div v-if="matchingUsages.length > 0" class="product-usages__source-mention">
        {{ i18n._('Source&nbsp;:') }} {{ matchingUsages[0].source?.toUpperCase() }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ref, defineComponent } from 'vue'
import type { PropType, Ref } from 'vue'
import { i18n } from '@b2ag/locale'
import type { SingleProduct } from '@b2ag/product/src/presenters/typings'
import { usagesService } from '../../index'
import PProductUsageDetails from '../PProductUsageDetails/PProductUsageDetails.vue'
import type { Usage } from '@b2ag/module-product-sdk'

export default defineComponent({
  name: 'PProductUsages',
  components: { PProductUsageDetails },
  props: {
    product: {
      type: Object as PropType<SingleProduct>,
      default: () => ({}),
    },
  },
  setup(props) {
    const { product } = props

    const selected_crop_id = ref('')
    const selected_target_id = ref('')
    const usages: Ref<Usage[]> = ref([])
    const matchingUsages: Ref<Usage[]> = ref([])

    const removeDuplicatedTargets = () => {
      return usages.value.filter((obj, pos, arr) => {
        return arr.map((usage) => usage.target_id).indexOf(obj.target_id) === pos
      })
    }

    const extractTargetsFromUsages = () => {
      if (usages.value.length > 0) {
        return removeDuplicatedTargets().sort((firstTarget, secondTarget) => {
          return (firstTarget.target_name ?? '').localeCompare(secondTarget.target_name ?? '')
        })
      }
      return []
    }

    const targets = computed(() => extractTargetsFromUsages())
    const areCropAndTargetSelected = computed(() => selected_crop_id.value !== '' && selected_target_id.value !== '')
    const isProductKnown = computed(() => product?.name)
    // Dev note: do not mute the store product
    const crops = computed(() =>
      product.usages_by_crops
        ? [...product.usages_by_crops].sort((firstCrop, secondCrop) =>
            firstCrop.crop_name.localeCompare(secondCrop.crop_name),
          )
        : undefined,
    )

    const fetchMatchingUsages = (event: Event) => {
      const selectedTargetId = (event.target as HTMLInputElement).value
      const allTargetsId = '-1'
      if (usages.value && selectedTargetId) {
        matchingUsages.value = usages.value.filter((usage) =>
          [selectedTargetId, allTargetsId].includes(usage.target_id ?? ''),
        )
      }
    }
    const fetchUsages = async (event: Event) => {
      const selectedCropId = (event.target as HTMLInputElement).value
      if (selectedCropId) {
        const foundUsages = await usagesService.findByProductIdAndCropId(product._id, selectedCropId)
        usages.value = foundUsages[0].usages
        matchingUsages.value = []
        selected_target_id.value = ''
      }
    }

    return {
      selected_crop_id,
      selected_target_id,
      usages,
      matchingUsages,
      targets,
      areCropAndTargetSelected,
      isProductKnown,
      crops,
      fetchMatchingUsages,
      fetchUsages,
      i18n,
    }
  },
})
</script>

<style lang="scss">
.product-usages {
  display: flex;
  width: 100%;
  min-height: 192px;
  flex-direction: column;
  background-color: var(--color-text-light);

  @media (min-width: $breakpoint-md) {
    flex-direction: row;
  }
}

.product-usages__selects {
  padding: 2rem;

  @media (min-width: $breakpoint-md) {
    flex: 0 0 50%;
  }
}

.product-usages__header {
  margin-bottom: 2rem;
  color: var(--color-white);
  font-size: 1.6rem;
  font-weight: bold;
  text-transform: uppercase;
}

.product-usages__select {
  width: 100%;
  height: 40px;
  text-indent: 2px;
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-1);
  font-size: 1.6rem;
  color: var(--color-black);

  &:invalid {
    color: var(--color-text-light);
  }

  &:not(:last-of-type) {
    margin-bottom: 1rem;
  }
}

.product-usages__results {
  position: relative;
  flex: 0 0 50%;
  padding: 1.5rem 0 0.5rem;
  font-size: 1.6rem;
  line-height: 2.4rem;

  @media (min-width: $breakpoint-md) {
    padding: 0;
  }
}

.product-usages__warning {
  color: var(--color-warning);
  font-weight: bold;
  text-align: center;
  padding: 2rem;
}

.product-usages__no-select {
  display: none;
  height: 100%;
  border: 1px solid var(--color-inactive-light);
  text-align: center;
  background-color: var(--color-white);

  @media (min-width: $breakpoint-md) {
    display: flex;
    align-items: center;
    justify-content: center;

    + .product-usages__source-mention {
      color: var(--color-inactive-dark);
    }
  }
}

.product-usages__source-mention {
  position: absolute;
  bottom: 1rem;
  right: 2rem;
  text-align: right;
  color: var(--color-inactive-light);

  @media (min-width: $breakpoint-md) {
    bottom: 0.5rem;
  }
}
</style>
